import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../components/layout';
import { Alert } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { api, errorMessage } from '../../lib/api';
import { useCan } from '../../lib/session';
import type { ImportRun, ImportRunSummary } from '../../lib/types';
import { formatDateTime } from '../../lib/utils';

/** Статус запуска: этап импорта важнее технического статуса задачи. */
export function RunStatusBadge({ run }: { run: ImportRunSummary }) {
  const { t } = useTranslation();
  // Проверенный файл ждёт решения, запуск при этом ещё «running» — важнее этап.
  const phase = run.stats.phase;
  if (phase === 'applied') return <Badge variant="success">{t('imports.phase.applied')}</Badge>;
  if (phase === 'staged') return <Badge variant="default">{t('imports.phase.staged')}</Badge>;
  if (phase === 'blocked') return <Badge variant="warning">{t('imports.phase.blocked')}</Badge>;
  if (phase === 'failed' || run.status === 'failed')
    return <Badge variant="destructive">{t('imports.phase.failed')}</Badge>;
  if (run.status === 'running')
    return <Badge variant="secondary">{t('imports.phase.running')}</Badge>;
  return <Badge variant="outline">{t(`imports.status.${run.status}`)}</Badge>;
}

function Stat({ label, value, tone }: { label: string; value: number | undefined; tone?: 'warn' }) {
  return (
    <div className="rounded-md border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={
          tone === 'warn' && value
            ? 'text-xl font-semibold text-amber-700'
            : 'text-xl font-semibold'
        }
      >
        {value ?? 0}
      </p>
    </div>
  );
}

export function ImportRunPage({ runId }: { runId: string }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const canRun = useCan('imports.run');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();

  const run = useQuery({
    queryKey: ['imports', 'run', runId],
    queryFn: () => api<ImportRun>(`/suppliers/import-runs/${runId}`),
    // Проверка файла и применение идут в фоне — обновляем, пока не закончится.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 2000;
      if (!data.stats.phase) return 2000;
      if (applying && data.stats.phase !== 'applied' && data.stats.phase !== 'failed') return 2000;
      return false;
    },
  });
  const data = run.data;
  const stats = data?.stats;

  const apply = async (forced: boolean) => {
    setError(undefined);
    try {
      await api(`/suppliers/import-runs/${runId}/${forced ? 'apply-forced' : 'apply'}`, {
        method: 'POST',
      });
      setApplying(true);
      setNotice(t('imports.applyQueued'));
      await queryClient.invalidateQueries({ queryKey: ['imports', 'run', runId] });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  if (run.error) return <Alert variant="destructive">{errorMessage(run.error)}</Alert>;

  return (
    <>
      <Link
        to="/suppliers"
        className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        {t('nav.suppliers')}
      </Link>
      <PageHeader
        title={`${t('imports.title')} · ${data ? formatDateTime(data.startedAt, i18n.language) : '…'}`}
        actions={data ? <RunStatusBadge run={data} /> : null}
      />
      {!data || !stats ? (
        <p className="text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <div className="grid gap-6">
          {stats.error || data.error ? (
            <Alert variant="destructive" title={t('imports.phase.failed')}>
              {stats.error ?? data.error}
            </Alert>
          ) : null}
          {stats.guard?.tripped ? (
            <Alert variant="warning" title={t('imports.guardTripped')}>
              {stats.guard.reasons.join('\n')}
              {'\n\n'}
              {t('imports.guardHint')}
            </Alert>
          ) : null}
          <Card>
            <CardHeader title={t('imports.rows')} />
            <CardContent className="grid gap-3 sm:grid-cols-4">
              <Stat label={t('imports.rows')} value={stats.rows?.total} />
              <Stat label={t('imports.valid')} value={stats.rows?.valid} />
              <Stat label={t('imports.invalid')} value={stats.rows?.invalid} tone="warn" />
              <Stat label={t('imports.warnings')} value={stats.rows?.warnings} tone="warn" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader title={t('imports.changes')} />
            <CardContent className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <Stat label={t('imports.willCreate')} value={stats.actions?.create} />
              <Stat label={t('imports.willUpdate')} value={stats.actions?.update} />
              <Stat label={t('imports.unchanged')} value={stats.actions?.unchanged} />
              <Stat label={t('imports.missing')} value={stats.missing} tone="warn" />
              <Stat label={t('imports.zeroed')} value={stats.zeroedStock} tone="warn" />
              <Stat
                label={t('imports.bigPriceChanges')}
                value={stats.bigPriceChanges}
                tone="warn"
              />
            </CardContent>
          </Card>
          {stats.applied ? (
            <Alert variant="success" title={t('imports.applied')}>
              {t('imports.appliedStats', stats.applied)}
              {stats.matched ? `\n${t('imports.matchedStats', stats.matched)}` : ''}
            </Alert>
          ) : null}
          {notice && stats.phase !== 'applied' ? <Alert>{notice}</Alert> : null}
          {error ? <Alert variant="destructive">{error}</Alert> : null}
          {canRun && (stats.phase === 'staged' || stats.phase === 'blocked') && !applying ? (
            <div className="flex gap-2">
              {stats.phase === 'staged' ? (
                <Button onClick={() => void apply(false)}>{t('imports.apply')}</Button>
              ) : (
                <Button variant="destructive" onClick={() => void apply(true)}>
                  {t('imports.applyForced')}
                </Button>
              )}
            </div>
          ) : null}
          {data.sample.length > 0 ? (
            <Card>
              <CardHeader title={t('imports.sample')} />
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-2 pr-3">{t('imports.row')}</th>
                      <th className="py-2 pr-3">{t('imports.action')}</th>
                      <th className="py-2 pr-3">{t('imports.errors')}</th>
                      <th className="py-2">{t('imports.data')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sample.map((s) => (
                      <tr key={s.rowNumber} className="border-b align-top last:border-0">
                        <td className="py-2 pr-3">{s.rowNumber}</td>
                        <td className="py-2 pr-3">
                          <Badge variant={s.action === 'error' ? 'destructive' : 'secondary'}>
                            {t(
                              `imports.actionName.${s.action as 'create' | 'update' | 'error' | 'unchanged'}`,
                              { defaultValue: s.action },
                            )}
                          </Badge>
                        </td>
                        <td className="py-2 pr-3 text-destructive">
                          {[...(s.errors?.errors ?? []), ...(s.errors?.warnings ?? [])].join('; ')}
                        </td>
                        <td className="max-w-lg py-2 font-mono text-xs text-muted-foreground">
                          {s.normalized ? JSON.stringify(s.normalized) : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}
    </>
  );
}
